import { InputTable, InputColumnTableProps } from "medere-ui-framework";
import { useLocationConfigContext } from "../../../context/LocationConfigContext";
import { activateSector } from "../../../../../servicesHooks/Location/useLocationConfigService";
import { LocationConfigTab } from "../../../MainLayoutConfigView";
import { Sector } from "../../../../../interface/interface";
import useResizeListener from "../../../../../hooks/useResizeListener";
import LocationMobileActionMenu, { LocationMobileActionItem } from "../../components/LocationMobileActionMenu";
import LocationActionMenu, { LocationActionItem } from "../../components/LocationActionMenu";

interface Props {
    sectors: Sector[];
    onChange: (newSectors: Sector[]) => void;
    onEdit: (sector: Sector) => void;
    onDelete: (sector: Sector, _idx: number) => void;
}

export function SectorTable({ sectors, onChange, onEdit, onDelete }: Props) {
    const isMobile = useResizeListener(768);
    const { setActiveTab } = useLocationConfigContext();

    const handleAddOffice = () => { //va a la pestaña de consultorios/salas. A definir si hace algo más o se quita.
        setActiveTab(LocationConfigTab.OFFICE.id)
    };

    const toggleActive = async (item: Sector, index: number) => {
    try {
        await activateSector(item.sector, !item.active);
        const updated = [...sectors];
        updated[index] = { ...item, active: !item.active };
        onChange(updated);
    } catch (err) {
        console.error("Error al cambiar estado del sector:", err);
    }
    };


    const columns: InputColumnTableProps<Sector>[] = [
        {
            header: "Nombre",
            className: "w-1/4 min-w-20",
            body: (item) => (item.name),
        },{
            header: "Abreviatura",
            className: "w-1/6 min-w-36",
            body: (item) => (item.shortname),
        },{
            header: "Tipo",
            className: "w-1/4 min-w-20",
            body: (item) => (item.sectortypedescription),
        },{
            header: "Cód. externo",
            className: "w-1/6 min-w-9",
            body: (item) => (item.externalcode),
        },{
            header: "Repositorio",
            className: "w-1/4 min-w-20",
            body: (item) => (item.repositorytypedescription),
        },{
            header: "Trabajo con",
            className: "w-1/4 min-w-20",
            body: (item) => (item.parentsectorname),
        },{
            header: "Traslados",
            className: "w-1/12 min-w-20",
            body: (item) => (<span>{item.acceptpatienttransfers ? "Sí" : "No"}</span>),
        },{
            header: "Estado",
            className: "w-1/12",
            body: (item) => (<span>{item.active ? "Activo" : "Inactivo"}</span>),
        },{
            header: "",
            className: "w-1/12",
            body: (item, index) => {
                const menuItems: LocationActionItem[] = [
                    {
                        label: "Editar",
                        icon: "pi pi-file-edit",
                        action: () => onEdit(item),
                    },{
                        label: "Crearle una sala",
                        icon: "pi pi-plus",
                        action: () => handleAddOffice(),
                    },{
                        label: item.active ? "Desactivar" : "Activar",
                        icon: item.active ? "pi pi-minus-circle" : "pi pi-check-circle",
                        action: () => toggleActive(item, index),
                    },{
                        label: "Eliminar",
                        icon: "pi pi-trash",
                        action: () => onDelete(item, index),
                    },
                ];
                return (
                    <div className="flex justify-content-end">
                        <LocationActionMenu items={menuItems} ariaLabel="Opciones del sector" buttonId="buttonMenuItemId" />
                    </div>
                );
            },
        },
    ];

    function formatCardValue(value: string | null | undefined, emptyText = "Sin datos") {
        const normalized = String(value ?? "").trim();
        return normalized !== "" ? normalized : emptyText;
    }

    //border-divider hace que se vea el divider en la tabla
    if (isMobile) {
        return (
            <div className="location-mobile-card-list">
                {(sectors ?? []).map((item, index) => {
                    const menuItems: LocationMobileActionItem[] = [
                        {
                            label: "Editar",
                            icon: "pi pi-file-edit",
                            action: () => onEdit(item),
                        },
                        {
                            label: "Crearle una sala",
                            icon: "pi pi-plus",
                            action: () => handleAddOffice(),
                        },
                        {
                            label: item.active ? "Desactivar" : "Activar",
                            icon: item.active ? "pi pi-minus-circle" : "pi pi-check-circle",
                            action: () => { void toggleActive(item, index); },
                        },
                        {
                            label: "Eliminar",
                            icon: "pi pi-trash",
                            action: () => onDelete(item, index),
                        },
                    ];

                    return (
                        <article key={`location-sector-${item.sector}-${index}`} className="location-mobile-card">
                            <div className="location-mobile-card-header">
                                <h4 className="location-mobile-card-title">{formatCardValue(item.name, "Sin nombre")}</h4>
                                <LocationMobileActionMenu items={menuItems} ariaLabel="Opciones del sector" />
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Abreviatura</span>
                                <span className="location-mobile-card-value">{formatCardValue(item.shortname)}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Tipo</span>
                                <span className="location-mobile-card-value">{formatCardValue(item.sectortypedescription)}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Código externo</span>
                                <span className="location-mobile-card-value">{formatCardValue(item.externalcode)}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Repositorio</span>
                                <span className="location-mobile-card-value">{formatCardValue(item.repositorytypedescription)}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Trabajo con</span>
                                <span className="location-mobile-card-value">{formatCardValue(item.parentsectorname)}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Traslados</span>
                                <span className="location-mobile-card-value">{item.acceptpatienttransfers ? "Sí" : "No"}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Estado</span>
                                <span className="location-mobile-card-value">{item.active ? "Activo" : "Inactivo"}</span>
                            </div>
                        </article>
                    );
                })}
                {(sectors ?? []).length === 0 && (
                    <p className="location-mobile-empty">No hay sectores cargados.</p>
                )}
            </div>
        );
    }

    return (
        <InputTable
            columnGap={2}
            headerDivClassName="font-semibold bg-bluegray-50"
            dividerClassName="my-1 border-divider"
            buttonColumnWidth={3}
            items={sectors ?? []}
            columns={columns}
            divider
        />
    );
}


